import LoggingDatabase from './LoggingDatabase';
import { FilterData, LogLevel, TerminalLog, TerminalLogViewModel } from './TerminalLog';

export class Logger {
    private static extensiveLoggingEnabled = false;

    static setEnableExtensiveLogging(value: boolean) {
        this.extensiveLoggingEnabled = value;
        this.log(`Extensive logging is ${value ? 'enabled' : 'disabled'}.`);
    }

    static async debug(message: string | any, filter?: FilterData, ...data: any) {
        console.debug(message, ...data, this.getSource());
        if (this.extensiveLoggingEnabled === true) await this.save(message, LogLevel.DEBUG, filter, ...data);
    }
    static async log(message: string | any, filter?: FilterData, ...data: any) {
        console.log(message, ...data, this.getSource());
        await this.save(message, LogLevel.INFO, filter, ...data);
    }
    static async warn(message: string | any, filter?: FilterData, ...data: any) {
        console.warn(message, ...data, this.getSource());
        await this.save(message, LogLevel.WARN, filter, ...data);
    }
    static async error(message: string | any, filter?: FilterData, ...data: any) {
        console.error(message, ...data, this.getSource());
        await this.save(message, LogLevel.ERROR, filter, ...data);
    }

    private static async save(message: string | any, level: LogLevel, filter?: FilterData, ...data: any) {
        let actualMessage = '';
        if (message instanceof Error) {
            actualMessage = message.message;
        }

        const log =
            typeof message === 'string'
                ? new TerminalLog(message, level, { ...filter }, data && JSON.stringify({ ...data }))
                : new TerminalLog(actualMessage, level, { ...filter }, data && JSON.stringify([message, { ...data }]));

        try {
            await this.saveLog(log);
        } catch (error) {
            console.log('log was not saved', error);
        }
    }

    private static async saveLog(log: TerminalLog): Promise<boolean> {
        return await LoggingDatabase.saveLog(log);
    }
    static async getLogs(): Promise<TerminalLogViewModel[] | undefined> {
        const logs = await LoggingDatabase.getLogs();
        return logs?.map(TerminalLog.parseToViewModel);
    }
    static async removeById(ids: number[]): Promise<void> {
        return LoggingDatabase.removeById(ids);
    }

    static async generateHash(value: string): Promise<string> {
        // Based on https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest#converting_a_digest_to_a_hex_string
        const msgUint8 = new TextEncoder().encode(value); // encode as (utf-8) Uint8Array
        const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8); // hash the message
        const hashArray = Array.from(new Uint8Array(hashBuffer)); // convert buffer to byte array
        // convert bytes to hex string
        return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
    }

    private static getSource() {
        try {
            return new Error().stack?.split('\n')[2].trim() || 'Unknown';
        } catch {
            return undefined;
        }
    }
}
